import { Location } from "@angular/common";
import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap, Params } from "@angular/router";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import { JobDto, JobStatusDto, LookupListEx } from "@shared/models";
import { combineLatest, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { NOT_ALPHA_NUMERIC_PLUS, PHONE_MASK, PHONE_REGEX } from "@modules/common";
import { AppStore, LoginStatus } from "../../common/app.store";
import { JobService, ProductSettingService, StorageService } from "../../common/services";

/**
 * Scenarios for Displaying this Component
 * 1. Customer job View - and confirm the telephone
 * 2. Customer job view with check params (sms urls) - summary, cod and validation
 * 3. Payment redirect with error message and url params
 */
@Component({
  selector: "abi-confirm-details",
  templateUrl: "./confirm-details.component.html",
  styleUrls: ["./confirm-details.component.scss"]
})
export class ConfirmDetailsComponent implements OnInit {
  static phoneRegex = PHONE_REGEX;
  phoneMask = PHONE_MASK;
  public isCod = false;// show COD page if job type is COD and has outstanding amounts
  public isValidation = false;// else, if job is closed and requires validation, show validation page
  public isFacebook = false;// scenario used to build a donations profile, online payment and invoice generation
  private checkParam = false;
  disableNav = true;

  private jobStatuses: LookupListEx<JobStatusDto>;

  job: JobDto;
  jobStatus: JobStatusDto;
  withCountrySelector = false;
  telephone = "";
  jobId: string;
  showConfirm = false;
  showBank = false;
  errorTitle: string;
  errorMessage: string;
  bankMessage = "";

  @ViewChild("paymentFailed")
  private paymentFailedTmpl: TemplateRef<any>;

  constructor(private jobService: JobService, private route: ActivatedRoute,
              private productSettings: ProductSettingService, lookups: LookupListService, private dialog: MatDialog,
              private location: Location,
              private storage: StorageService, private appStore: AppStore) {
    lookups.lookupListEx<JobStatusDto>("CodeJobStatus").subscribe(l => {
      this.jobStatuses = l;
      this.setJobStatus();
    });
    this.isFacebook = this.productSettings.hasSetting("VatRate");// CODE SMELL: an illogical correlation
  }

  ngOnInit() {
    // product settings
    this.withCountrySelector = this.productSettings.booleanValue('PhoneInputInternational');
    const useMask = this.productSettings.numericValue("PhoneMask") === 0;
    this.phoneMask = useMask ? (this.productSettings.stringValue("PhoneMask") || this.phoneMask) : "";
    // use params to setup local vars
    combineLatest([this.route.paramMap, this.route.queryParams]).pipe(
      switchMap(([paramMap, queryMap]: [ParamMap, Params]) => {
        this.jobId = paramMap.get("jobId");
        this.checkParam = !!paramMap.get("check") && paramMap.get("details") !== "true";
        this.disableNav = paramMap.get("inapp") !== "true";

        // Handle Error messaging
        const msg = queryMap.msg;
        const code = queryMap.code;
        const detail = queryMap.detail;
        const bankCode = queryMap.bCode;
        const bankMsg = queryMap.bMsg;
        if (msg && code) {
          this.errorTitle = msg;
          this.errorMessage = detail + " (" + code + ")";
          if (bankMsg || bankCode) {
            this.showBank = true;
            this.bankMessage = bankMsg + " (" + bankCode + ")";
          }
          this.showError();
        }
        this.job = null;
        // check and telephone may be used - either prompt for the tel, or use given 'check' param
        // note: no process to set the jobId found in local codebase
        this.telephone = this.storage.local.getItem(this.jobId) || paramMap.get("check");
        if (this.telephone /*|| (loggedIn === LoginStatus.True && !paramMap.get("check"))*/) {
          // Get Job and apply a variety of smart vars to show the necessary 'job page'
          return this.confirm(paramMap.get("details") === "true");
        }
        return null;// triggers 'failure mode'
      }))
      .subscribe(
        () => {
          // completed
        },
        () => {
          // failure mode
          // may be a scenario where jobId was invalid...
        this.telephone = "";
        this.showConfirm = !this.isFacebook;
        if (this.isFacebook && this.disableNav) {
          // For some reason we will update login status... (purpose isn't clear here)
          setTimeout(() => this.appStore.update({loginStatus: LoginStatus._}), 2);
        }
      });
  }

  private setJobStatus(job?: JobDto) {
    job = job || this.job;

    if (job && this.jobStatuses) {
      this.jobStatus = this.jobStatuses.item(job.statusId);
      // console.log(this.checkParam, this.jobStatus.labels, this.jobStatus.labels.some(l => !!l) );
      this.isValidation = this.checkParam && !!this.jobStatus.labels && !!this.jobStatus.labels.length &&
        this.jobStatus.labels.some(l => !!l);
    }
  }

  // URL was passed error params, show the dialog but also - remove params from url (for cleaner UX)
  private showError() {
    setTimeout(() => {
      this.dialog.open(this.paymentFailedTmpl);
      const url = this.location.path().split("?")[0];
      this.location.replaceState(url);
    }, 1);
  }

  confirmClick() {
    this.confirm().toPromise().then(_ => { })
    .catch(e => {
      // 404's/exceptions can usually be swallowed here as it's mainly concerned with 'wrong tel number'
    });
  }

  back() {
    this.location.back();
  }

  defaultCodTab = 'cod';
  confirm(details?: boolean): Observable<JobDto> {
    const telephone = this.telephone.replace(NOT_ALPHA_NUMERIC_PLUS, "");

    // request job with 'check/telephone' var
    return this.jobService.getJob(this.jobId, telephone).pipe(map(job => {
      this.setJobStatus(job);
      this.job = job;
      this.showConfirm = false;// job is valid with check code - no need to confirm details anymmore
      this.defaultCodTab = this.productSettings.stringValue("JobCODDefaultTab") || 'cod';
      const jobTypes = this.productSettings.stringValue("JobTypesCOD");
      const jobStatuses = this.productSettings.stringValue("JobStatusCOD");
      const hasOutstandingAmount = this.job.unpaid > 0;
      this.isCod = !this.isFacebook && !details && hasOutstandingAmount && this.productSettings.booleanValue("JobTypesCOD") &&
        /*this.job.lines.length &&*/ jobTypes.includes(this.job.typeId) /*&& (!jobStatuses || jobStatuses.includes(this.job.statusId))*/;

      if (this.isCod) {
        this.isCod = !this.isFacebook;
      }
      if ((this.isFacebook || this.isCod || this.isValidation) && this.disableNav) {
        this.appStore.update({loginStatus: LoginStatus._});
      }
      return job;
    }));
  }
}
